import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/cn'

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  /** Render a custom control (e.g. shadcn Select) instead of the default input. */
  control?: ReactNode
  containerClassName?: string
  /**
   * Show/hide eye toggle for password fields. Defaults to true when
   * `type='password'`. Pass `false` to opt out (e.g. when the field is
   * intentionally always masked).
   */
  showPasswordToggle?: boolean
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  {
    id,
    name,
    label,
    hint,
    error,
    control,
    children,
    containerClassName,
    className,
    required,
    type,
    showPasswordToggle,
    ...rest
  },
  ref,
) {
  const autoId = useId()
  const customControl = control ?? children;
  const inputId = id || name || (customControl ? undefined : autoId)
  const hintId = hint ? `${inputId}-hint` : null
  const describedBy = [hintId, error ? `${inputId}-error` : null]
    .filter(Boolean)
    .join(' ') || undefined

  const [revealed, setRevealed] = useState(false)
  const isPassword = type === 'password'
  const enableToggle = isPassword && showPasswordToggle !== false && !customControl
  const effectiveType = enableToggle && revealed ? 'text' : type

  const inputEl = (
    <Input
      ref={ref}
      id={inputId}
      name={name}
      type={effectiveType}
      required={required}
      aria-invalid={!!error}
      aria-describedby={describedBy}
      className={cn(
        error && 'border-destructive focus-visible:ring-destructive',
        enableToggle && 'pr-10',
        className,
      )}
      {...rest}
    />
  )

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      <Label htmlFor={inputId} className={error ? 'text-destructive' : undefined}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {customControl ?? (
        enableToggle ? (
          <div className="relative">
            {inputEl}
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              aria-pressed={revealed}
              className="absolute inset-y-0 right-0 flex items-center rounded-sm px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              tabIndex={-1}
            >
              {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        ) : (
          inputEl
        )
      )}
      {/* Hints render inline below the control so they stay visible inside
          modals / popovers / scroll containers and on touch devices that
          can't trigger hover tooltips. Wraps to as many lines as needed. */}
      {hint ? (
        <p id={hintId ?? undefined} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
})
